import {
  TypographyCaption,
  TypographyH2,
  TypographyH4,
  TypographyP2,
} from '@heidi/design-system/typography';
import { formatCjkAwareDisplayName, getCjkAwareDisplayInitials } from '@heidi/utils/i18n';
import Image from 'next/image';
import type { OnboardingEmrOption } from '../../../onboarding-host';
import { UserBadgeIcon } from './assets/user-badge-svg';

import { SkeletonBlock, SkeletonLine, type SkeletonTone } from './PreviewPanelContent';

/**
 * `sand` is the control card. `forest` is the guided-tour card, which sits on a
 * photographic background and so carries its own fixed dark-green artwork.
 */
export type BadgeCardVariant = 'sand' | 'forest';

const VARIANT_STYLES = {
  sand: {
    avatar: 'bg-background-primary',
    avatarContent: 'text-text-primary-invert',
    text: 'text-text-secondary',
    skeleton: 'default',
  },
  forest: {
    // theme-agnostic: the forest card is fixed brand artwork on a photo, so it
    // must not flip with the theme — hence primitives plus the on-dark text tokens.
    avatar: 'bg-forest-700',
    avatarContent: 'text-text-primary-on-dark',
    text: 'text-text-primary-on-dark',
    skeleton: 'on-forest',
  },
} as const satisfies Record<
  BadgeCardVariant,
  { avatar: string; avatarContent: string; text: string; skeleton: SkeletonTone }
>;

interface PersonalizationBadgeCardProps {
  firstName: string;
  lastName: string;
  specialtyName?: string;
  clinicName?: string;
  /** The selected provider (id, display name, icon), resolved by useSelectedEmrOption. */
  emrEhr?: OnboardingEmrOption | null;
  displayLanguage?: string | null;
  variant?: BadgeCardVariant;
  /** Shown in place of the person's name, e.g. a team name. */
  heading?: string;
  /**
   * Shown in place of the specialty line. When set the clinic line is dropped,
   * so the card carries two lines instead of three.
   */
  subheading?: string;
  /** Replaces the initial or placeholder glyph in the avatar circle, e.g. the Heidi mark on a team card. */
  avatar?: React.ReactNode;
}

export function PersonalizationBadgeCard({
  firstName,
  lastName,
  specialtyName,
  clinicName,
  emrEhr,
  displayLanguage,
  variant = 'sand',
  heading,
  subheading,
  avatar,
}: PersonalizationBadgeCardProps) {
  const hasName = heading || firstName || lastName;
  const initials = getCjkAwareDisplayInitials({ firstName, lastName }, displayLanguage);
  // The person's own initial when there is one; a heading-only card shows the heading's.
  const firstInitial =
    firstName || lastName
      ? ([...initials].at(0) ?? '')
      : heading
        ? ([...heading].at(0) ?? '')
        : '';
  const fullName =
    heading ?? formatCjkAwareDisplayName({ firstName, lastName }, displayLanguage);
  const secondLine = subheading ?? specialtyName;
  // A subheading replaces the specialty line and drops the clinic line with it.
  const hasClinicLine = subheading === undefined;
  const styles = VARIANT_STYLES[variant];

  return (
    <div className="relative h-[456px] w-[315px] overflow-hidden rounded-[22px] shadow-lg">
      <BadgeCardBackground variant={variant} />

      <div className="absolute inset-x-0 top-[52px] flex h-[404px] flex-col items-center gap-6 overflow-hidden px-6 pb-6 pt-14">
        {/* Avatar */}
        <div
          className={`flex size-[65px] shrink-0 items-center justify-center rounded-full ${styles.avatar}`}
        >
          {avatar ??
            (hasName ? (
              <TypographyH2 className={styles.avatarContent}>{firstInitial}</TypographyH2>
            ) : (
              <UserBadgeIcon size={40} className={styles.avatarContent} />
            ))}
        </div>

        {/* Progressive content — 8px gap between items */}
        <div className="flex w-full min-w-0 flex-col items-center gap-2">
          {hasName ? (
            <TypographyH2
              className={`line-clamp-2 w-full break-words text-center ${styles.text}`}
            >
              {fullName}
            </TypographyH2>
          ) : (
            <div className="py-2">
              <SkeletonBlock width="w-[197px]" variant={styles.skeleton} />
            </div>
          )}

          {secondLine ? (
            <TypographyH4
              className={`line-clamp-2 w-full break-words text-center ${styles.text}`}
            >
              {secondLine}
            </TypographyH4>
          ) : (
            <div className="py-2">
              <SkeletonBlock width="w-[228px]" variant={styles.skeleton} />
            </div>
          )}

          {hasClinicLine &&
            (clinicName ? (
              <TypographyP2
                className={`line-clamp-2 w-full break-words text-center ${styles.text}`}
              >
                {clinicName}
              </TypographyP2>
            ) : (
              <div className="py-2">
                <SkeletonLine width="w-[142px]" variant={styles.skeleton} />
              </div>
            ))}
        </div>

        {emrEhr && <EmrBadgeIcon emrEhr={emrEhr} />}
      </div>
    </div>
  );
}

function EmrBadgeIcon({ emrEhr }: { emrEhr: OnboardingEmrOption }) {
  // A provider with no Heidi icon renders the initial letter below, the
  // existing missing-logo behavior.
  return (
    <div className="flex size-10 items-center justify-center overflow-hidden rounded-full bg-surface shadow-md">
      {emrEhr.icon ? (
        <Image
          src={emrEhr.icon}
          alt={emrEhr.name}
          width={40}
          height={40}
          className="size-[70%] object-contain"
        />
      ) : (
        <TypographyCaption className="text-text-secondary">
          {emrEhr.name.charAt(0)}
        </TypographyCaption>
      )}
    </div>
  );
}

// theme-agnostic: both palettes are fixed brand artwork — the sand card reads
// against the pattern backgrounds, the forest card against the tour photos.
const BADGE_CARD_FILLS = {
  sand: { card: 'fill-sand-150', decoration: 'fill-sand-300' },
  forest: { card: 'fill-forest-800', decoration: 'fill-forest-700' },
} as const satisfies Record<BadgeCardVariant, { card: string; decoration: string }>;

function BadgeCardBackground({ variant }: { variant: BadgeCardVariant }) {
  const fills = BADGE_CARD_FILLS[variant];

  return (
    <svg
      className="absolute inset-0 size-full"
      viewBox="0 0 315 456"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M293 0C305.15 0 315 9.84974 315 22V434C315 446.15 305.15 456 293 456H22C9.84974 456 5.47655e-07 446.15 0 434V22C0 9.84974 9.84974 5.59699e-07 22 0H293ZM111 31C105.477 31 101 35.4772 101 41C101 46.5228 105.477 51 111 51H204C209.523 51 214 46.5228 214 41C214 35.4772 209.523 31 204 31H111Z"
        className={fills.card}
      />
      <g clipPath="url(#clip0_badge)">
        <path
          d="M178.461 428.33C160.45 425.318 142.217 426.895 124.354 430.211C117.834 431.187 111.426 430.07 105.906 426.648C59.8131 395.49 -21.6434 354.958 -58.2277 418.878C-84.9556 466.332 -73.3824 535.137 -39.8098 577.904C-36.6057 582.259 -34.4301 586.812 -33.9492 591.966C-33.5792 595.289 -33.8382 598.782 -34.1045 602.154C-35.0147 612.293 -34.7187 622.474 -33.8234 632.584C-32.9206 641.421 -36.2875 649.248 -41.9483 656.191C-75.5505 699.134 -84.9778 772.719 -53.6991 819.028C-27.615 856.082 20.1948 849.514 57.4082 831.676C76.5587 823.255 92.261 812.106 108.814 802.759C113.291 800.737 117.938 800.264 122.926 800.744C143.785 804.393 164.668 805.503 185.638 801.664C191.765 800.61 198.322 799.861 204.19 801.982C206.676 802.83 209.059 804.089 211.427 805.58C253.938 834.914 317.902 863.59 356.639 832.298C370.928 820.802 379.497 802.356 383.878 784.985C395.443 738.818 382.02 686.033 351.785 648.944C345.813 638.586 350.512 625.705 349.854 614.527C350.024 602.303 345.865 590.284 353.783 579.339C385.165 539.337 396.687 483.943 380.733 435.839C355.463 356.181 268.715 385.005 210.072 425.934C199.92 432.608 190.182 430.282 178.601 428.359L178.453 428.337L178.461 428.33ZM176.995 433.279C202.791 436.348 193.312 440.979 179.896 453.125C154.678 475.876 163.365 480.62 138.628 457.515C135.061 454.2 131.428 450.82 128.246 447.858C124.879 444.705 121.934 442.117 120.683 440.286C118.301 437.133 122.052 436.023 125.086 435.372C128.653 434.601 132.715 434.015 136.423 433.491C149.927 431.611 163.35 431.469 176.833 433.251L176.995 433.272L176.995 433.279ZM-63.5112 519.866C-74.6848 472.278 -67.4626 393.058 -2.96642 389.219C33.8473 388.088 69.6029 407.954 98.9207 427.277C108.008 433.329 103.827 436.044 95.0063 439.331C34.8537 462.076 -10.8027 512.054 -28.8285 571.209C-29.9311 574.001 -31.189 575.861 -32.7504 576.158C-35.8805 576.518 -38.5888 572.531 -40.4535 570.162C-51.4792 555.139 -59.2489 537.902 -63.4816 520L-63.5112 519.866ZM-25.6022 593.14C-21.7618 591.733 -13.5998 601.214 -10.1738 603.589C-8.9454 604.622 -7.68005 605.647 -6.42209 606.637C-1.85644 610.518 2.8054 612.865 0.592871 617.383C-1.91564 621.455 -6.38509 624.382 -10.1516 627.811C-12.8155 630.081 -15.5386 632.428 -17.8991 634.422C-20.8664 636.925 -23.2417 638.636 -24.8623 638.897C-29.0283 639.633 -28.4955 630.123 -28.8877 627.43C-29.0061 622.021 -30.6933 594.688 -25.6688 593.168L-25.6022 593.14ZM30.9171 837.24C-35.7769 856.888 -68.987 810.685 -68.0768 748.016C-68.2026 726.283 -63.8367 704.755 -54.5796 684.817C-51.5976 678.369 -39.7136 655.547 -33.2684 654.352C-30.1087 653.928 -28.3549 660.114 -27.393 663.169C-9.48558 722.069 38.5166 771.361 98.6692 792.812C101.511 794.042 103.464 795.35 103.864 796.863C104.485 800.186 99.6311 803.007 96.9228 804.987C76.8251 818.54 54.833 830.651 31.0724 837.191L30.9097 837.233L30.9171 837.24ZM120.876 792.861C120.328 789.284 124.894 786.152 127.402 783.699C134.062 777.859 140.537 771.814 146.634 765.472C150.423 761.442 155.847 756.175 161.693 759.823C164.016 761.138 166.184 763.294 168.094 765.246C174.546 771.92 181.347 778.375 188.384 784.483C190.581 786.484 193.852 789.213 194.022 791.992C194.969 801.869 122.681 801.437 120.898 792.932L120.876 792.861ZM377.869 708.191C391.144 759.767 380.748 841.319 314.557 841.885C287.474 842.182 261.797 830.071 238.413 817.67C230.843 813.492 223.777 809.172 217.428 804.895C215.104 803.261 212.107 801.324 211.256 798.715C210.709 796.446 212.529 794.968 215.718 793.342C275.93 771.595 324.354 722.571 342.343 663.303C344.555 655.879 347.049 650.139 353.724 658.566C365.075 673.229 373.266 690.36 377.847 708.056L377.884 708.191L377.869 708.191ZM341.122 637.9C338.295 639.915 332.324 633.269 329.334 630.965C327.617 629.466 325.819 627.903 324.095 626.397C321.357 623.972 318.671 621.767 316.192 619.327C313.846 617.1 312.773 614.788 315.341 612.194C319.152 608.192 323.718 604.671 327.913 600.889C331.295 598.117 335.861 593.31 339.309 592.412C343.438 591.634 343.157 600.634 343.601 603.314C344.111 609.627 344.948 633.743 341.174 637.851L341.122 637.893L341.122 637.9ZM338.724 584.663C335.602 591.91 326.633 597.905 320.292 603.102C316.244 606.714 305.448 611.522 308.474 617.602C310.938 621.83 316.214 624.566 320.255 627.847C327.21 633.46 336.216 639.364 338.924 647.664C340.493 652.769 339.072 657.866 337.614 662.935C322.038 716.71 280.037 762.439 226.759 784.893C218.027 788.337 207.838 793.901 198.632 789.128C191.677 785.325 185.986 779.336 180.37 774.034C175.338 769.099 170.58 763.839 165.933 758.565C160.723 752.576 156.284 751.049 150.882 756.677C142.342 766.299 133.603 775.922 123.488 784.087C115.851 790.656 107.978 792.345 98.1734 788.711C38.0134 767.282 -9.0342 716.724 -24.1445 656.862C-24.9141 653.462 -25.2471 650.471 -24.5663 647.777C-23.6931 644.228 -21.0144 641.365 -18.2691 638.848C-12.7341 633.8 -4.39456 627.21 2.05062 622.636C4.06335 621.116 6.60887 619.554 8.13322 617.751C9.84996 615.644 9.02118 614.06 7.00845 612.017C4.26315 609.359 0.326481 606.849 -2.87761 604.459C-7.59125 601.009 -12.8155 597.12 -16.6855 593.564C-19.0683 591.365 -21.1994 588.856 -22.5979 585.978C-25.2988 580.668 -24.4035 574.489 -22.7533 568.84C-5.98551 510.343 41.2989 461.27 101.096 441.29C104.345 440.406 107.527 440.038 110.568 440.463C115.074 441.042 119.115 443.432 122.585 446.225C131.494 453.535 139.804 461.708 147.278 470.404C149.772 473.409 153.19 476.951 156.062 477.439C160.524 478.323 163.987 474.278 166.776 471.33C175.523 461.581 184.373 451.987 194.903 444.061C200.245 439.982 206.572 438.886 213.18 441.071C274.006 460.881 322.275 511.7 338.473 571.074C339.649 575.479 340.441 580.435 338.761 584.55L338.717 584.663L338.724 584.663ZM299.269 390.499C385.099 375.991 397.286 486.008 369.803 545.438C367.154 551.186 353.931 576.787 347.9 576.419C344.859 576.356 343.061 570.261 342.076 567.129C324.435 508.526 276.803 460.259 217.494 438.221C214.705 436.949 212.699 435.584 212.144 434.05C211.057 430.374 216.547 427.263 219.5 425.191C243.675 409.602 270.084 394.889 299.114 390.527L299.276 390.506L299.269 390.499Z"
          className={fills.decoration}
        />
      </g>
      <defs>
        <clipPath id="clip0_badge">
          <rect y="52" width="315" height="404" rx="22" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}
